import React from 'react';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';

import { Badge } from '~/shadcn/components/ui/Badge';

/**
 * SummaryAlerts
 *
 * Displays risk-level summary counts for the dashboard.
 * Shows critical, at-risk, and on-track badges alongside
 * total route/shipment/throughput stats.
 *
 * @param {Object} props
 * @param {Object} props.summary - Summary data from the API
 * @param {number} props.summary.critical - Count of critical routes
 * @param {number} props.summary.atRisk - Count of at-risk routes
 * @param {number} props.summary.onTrack - Count of on-track routes
 * @param {number} props.summary.totalRoutes - Total route count
 * @param {number} props.summary.totalShipments - Total shipment count
 * @param {number} props.summary.facilityThroughput - Facility throughput per hour
 */
function SummaryAlerts({ summary }) {
    const {
        atRisk = 0,
        critical = 0,
        facilityThroughput = 0,
        onTrack = 0,
        totalRoutes = 0,
        totalShipments = 0,
    } = summary || {};

    return (
        <div className="space-y-3">
            {/* Risk Summary Badges */}
            <div className="flex gap-3 flex-wrap">
                {/* Critical Issues */}
                <div className="flex items-center gap-2 px-4 py-2.5 bg-red-50 border border-red-200 rounded-lg">
                    <XCircle className="h-5 w-5 text-red-500" />
                    <span className="text-2xl font-bold text-red-600">{critical}</span>
                    <span className="text-sm font-medium text-red-700">Critical Issues</span>
                </div>

                {/* At Risk */}
                <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    <span className="text-2xl font-bold text-amber-600">{atRisk}</span>
                    <span className="text-sm font-medium text-amber-700">At Risk</span>
                </div>

                {/* On Track */}
                <div className="flex items-center gap-2 px-4 py-2.5 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    <span className="text-2xl font-bold text-green-600">{onTrack}</span>
                    <span className="text-sm font-medium text-green-700">On Track</span>
                </div>
            </div>

            {/* Stats Summary */}
            <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                    {totalRoutes} routes
                </Badge>
                <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                    {totalShipments} shipments
                </Badge>
                <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                    {facilityThroughput}/hr throughput
                </Badge>
            </div>
        </div>
    );
}

export default SummaryAlerts;
